import { EditorContent, useEditor } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import React, {
    forwardRef,
    memo,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
    useCallback,
} from 'react';
import TurndownService from 'turndown';
import { gfm } from 'turndown-plugin-gfm';

import './AcademicEditor.scss';
import { findParentNode } from '@tiptap/react';
import { message } from 'antd';
import { debounce } from 'lodash';
import { processContent } from './AcademicEditorUtil';
import EditorScroller from './EditorScrollBar';
import { extensions } from './customConfig/extensions';
import { shouldShowBubbleMenu, shouldShowButtons } from './bubbleMenuUtils';

// 创建 turndown 实例用于转换表格
const tableConverter = new TurndownService({
    headingStyle: 'atx',
    codeBlockStyle: 'fenced',
});
tableConverter.use(gfm);

/**
 * 学术编辑器组件
 * @param {Object} props
 * @param {Object} props.data - 编辑器数据
 * @param {Function} props.onOpenModal - 打开模态框的回调
 * @param {Function} props.getWordCount - 获取字数的回调
 */
const AcademicEditor = forwardRef(({ data, onOpenModal, getWordCount, onSaveSingChapter }, ref) => {
    const editingChapterId = useRef('');

    // 字数统计工具函数
    const countWords = useCallback((text) => {
        if (!text) return 0;

        // 移除HTML标签
        text = text.replace(/<[^>]*>/g, '');

        // 移除特殊字符和多余空格
        text = text.replace(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?~]/g, '');
        text = text.replace(/\s+/g, ' ').trim();

        // 匹配中文字符
        const cnMatches = text.match(/[\u4e00-\u9fa5]/g) || [];

        // 匹配英文单词
        const enMatches = text.match(/[a-zA-Z]+/g) || [];

        // 匹配单个数字字符
        const numMatches = text.match(/\d/g) || [];

        // 中文字符数 + 英文单词数 + 数字字符数
        return cnMatches.length + enMatches.length + numMatches.length;
    }, []);

    // 更新字数的函数 - 使用防抖
    const debouncedUpdateWordCount = useCallback(
        debounce((content) => {
            const count = countWords(content);
            getWordCount(count);
        }, 500),
        [countWords, getWordCount],
    );

    // 初始化编辑器
    const editor = useEditor({
        extensions,
        content: '',
        editorProps: {
            attributes: {
                class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none',
            },
            parseOptions: {
                preserveWhitespace: 'full',
            },
            enableInputRules: false,
            enablePasteRules: false,
            handleDOMEvents: {
                input: (view, event) => {
                    const content = view.state.doc.textContent;
                    debouncedUpdateWordCount(content);
                    return false;
                },
                keydown: (view, event) => {
                    // 检查当前光标是否在标题内
                    const { selection } = view.state;
                    const { $from } = selection;
                    let inHeading = false;

                    // 遍历节点层次结构，检查是否在标题中
                    for (let i = $from.depth; i > 0; i--) {
                        if ($from.node(i).type.name === 'heading') {
                            inHeading = true;
                            break;
                        }
                    }

                    // 如果在标题中，需要区分允许的键盘操作和不允许的键盘操作
                    if (inHeading) {
                        // 允许选择操作键 (方向键、Home、End、Page Up/Down)
                        const allowedKeys = [
                            'ArrowUp',
                            'ArrowDown',
                            'ArrowLeft',
                            'ArrowRight',
                            'Home',
                            'End',
                            'PageUp',
                            'PageDown',
                        ];

                        // 允许组合键 (Ctrl+A, Shift+方向键等)
                        if (
                            ((event.ctrlKey || event.metaKey) && event.key === 'a') ||
                            (event.shiftKey && allowedKeys.includes(event.key)) ||
                            allowedKeys.includes(event.key)
                        ) {
                            return false; // 允许这些键操作
                        }

                        // 阻止可能修改内容的键
                        event.preventDefault();
                        return true;
                    }

                    // 添加保护逻辑：阻止删除标题
                    // 1. 检查当前是否处于删除标题的边界位置
                    if (event.key === 'Backspace' || event.key === 'Delete') {
                        const doc = view.state.doc;
                        const { from, to, empty } = selection;

                        // 如果是选区删除，检查选区是否包含标题
                        if (!empty) {
                            let hasHeading = false;
                            view.state.doc.nodesBetween(from, to, (node) => {
                                if (node.type.name === 'heading') {
                                    hasHeading = true;
                                    return false;
                                }
                            });

                            if (hasHeading) {
                                event.preventDefault();
                                return true;
                            }
                        } else {
                            // 检查单个光标位置
                            // 如果Backspace键按下时，光标前面是标题
                            if (event.key === 'Backspace' && from > 0) {
                                const $before = doc.resolve(from - 1);
                                for (let i = $before.depth; i > 0; i--) {
                                    if ($before.node(i).type.name === 'heading') {
                                        event.preventDefault();
                                        return true;
                                    }
                                }

                                // 检查光标是否位于标题之后的位置
                                let pos = from - 1;
                                while (pos > 0) {
                                    const node = doc.nodeAt(pos);
                                    if (node && node.type.name === 'heading') {
                                        if (pos + node.nodeSize === from) {
                                            // 光标刚好在标题后，阻止删除
                                            event.preventDefault();
                                            return true;
                                        }
                                        break;
                                    }
                                    pos--;
                                }
                            }

                            // 如果Delete键按下时，光标后面是标题
                            if (event.key === 'Delete' && to < doc.content.size) {
                                const $after = doc.resolve(to + 1);
                                for (let i = $after.depth; i > 0; i--) {
                                    if ($after.node(i).type.name === 'heading') {
                                        event.preventDefault();
                                        return true;
                                    }
                                }

                                // 检查光标是否位于标题之前的位置
                                let pos = to;
                                while (pos < doc.content.size) {
                                    const node = doc.nodeAt(pos);
                                    if (node && node.type.name === 'heading') {
                                        if (pos === to) {
                                            // 光标刚好在标题前，阻止删除
                                            event.preventDefault();
                                            return true;
                                        }
                                        break;
                                    }
                                    if (!node) break;
                                    pos += node.nodeSize;
                                }
                            }
                        }
                    }

                    return false;
                },
            },
        },
    });

    // 获取当前章节的内容
    const getCurrentChapterContent = useCallback(
        (chapterId) => {
            if (!editor || !chapterId) return null;

            // 存储章节信息
            const chapter = {
                chapter: '',
                id: chapterId,
                content: '',
                sections: [],
            };

            let currentSection = null;
            let currentSubSection = null;
            let foundStart = false;
            let startLevel = 0;

            // 直接遍历所有节点
            editor.state.doc.forEach((node, offset, index) => {
                // 找到目标章节的开始
                if (node.type.name === 'heading' && node.attrs.id === chapterId) {
                    foundStart = true;
                    startLevel = node.attrs.level;
                    chapter.chapter = node.textContent;
                    return;
                }

                // 如果还没找到开始节点，继续查找
                if (!foundStart) return;

                // 如果遇到同级或更高级的标题，说明当前章节结束
                if (node.type.name === 'heading' && node.attrs.level <= startLevel) {
                    foundStart = false;
                    return false;
                }

                // 处理章节内容
                if (foundStart) {
                    if (node.type.name === 'heading') {
                        const level = node.attrs.level;
                        if (level === startLevel + 1) {
                            currentSection = {
                                chapter: node.textContent,
                                id: node.attrs.id || '',
                                content: '',
                                subSections: [],
                            };
                            chapter.sections.push(currentSection);
                            currentSubSection = null;
                        } else if (level === startLevel + 2) {
                            currentSubSection = {
                                chapter: node.textContent,
                                id: node.attrs.id || '',
                                content: '',
                            };
                            if (currentSection) {
                                currentSection.subSections.push(currentSubSection);
                            }
                        }
                    } else {
                        const content = getNodeContent(node);
                        if (content) {
                            if (currentSubSection) {
                                currentSubSection.content += content;
                            } else if (currentSection) {
                                currentSection.content += content;
                            } else {
                                chapter.content += content;
                            }
                        }
                    }
                }
            });

            return chapter;
        },
        [editor],
    );

    // 辅助函数：根据节点类型获取内容
    const getNodeContent = (node) => {
        if (node.type.name === 'paragraph') {
        }
        switch (node.type.name) {
            case 'paragraph':
                return `[AIML_TEXT]${node.textContent}[/AIML_TEXT]\n`;

            case 'table':
                const tableId = node.attrs.aimlTableId;
                const tableHtml = generateTableHtml(node, tableId);
                const tableMarkdown = tableConverter.turndown(tableHtml);
                return `[AIML_TABLE id=${tableId}]${tableMarkdown}[/AIML_TABLE]\n`;

            case 'image':
                const imageId = node.attrs.alt.split('-')[1];
                const imageSrc = node.attrs.src;
                const type = node.attrs.alt.split('-')[0];
                const typeEnum = {
                    echarts: 'ECHARTS',
                    mermaid: 'MERMAID',
                    latex: 'LATEX',
                };
                return `[AIML_${typeEnum[type]} id=${imageId}]${imageSrc}[/AIML_${typeEnum[type]}]\n`;

            default:
                return '';
        }
    };

    // 辅助函数：生成表格HTML
    const generateTableHtml = (tableNode, tableId) => {
        let tableHtml = `<table data-table-id="${tableId}">`;
        tableNode.content.forEach((row) => {
            tableHtml += '<tr>';
            row.content.forEach((cell) => {
                const tag = cell.type.name === 'tableHeader' ? 'th' : 'td';
                const attrs = {
                    colspan: cell.attrs.colspan,
                    rowspan: cell.attrs.rowspan,
                };

                const attrString = Object.entries(attrs)
                    .filter(([_, value]) => value)
                    .map(([key, value]) => `${key}="${value}"`)
                    .join(' ');

                tableHtml += `<${tag}${attrString ? ' ' + attrString : ''}>`;
                cell.content.forEach((paragraph) => {
                    if (paragraph.content) {
                        paragraph.content.forEach((text) => {
                            tableHtml += text.text;
                        });
                    }
                });
                tableHtml += `</${tag}>`;
            });
            tableHtml += '</tr>';
        });
        tableHtml += '</table>';
        return tableHtml;
    };

    // 自动保存的防抖函数
    const debouncedAutoSave = useCallback(
        debounce(async (chapterId) => {
            console.log('debouncedAutoSave被调用，章节ID:', chapterId);
            console.log('editor状态:', !!editor);

            if (!editor || !chapterId) {
                console.warn('自动保存条件不满足:', {
                    hasEditor: !!editor,
                    hasChapterId: !!chapterId,
                });
                return;
            }

            const chapterContent = getCurrentChapterContent(chapterId);
            if (chapterContent) {
                console.log('获取到章节内容，准备保存:', {
                    chapter: chapterContent.chapter,
                    id: chapterContent.id,
                    contentPreview: chapterContent.content,
                });
                onSaveSingChapter({
                    chapterId: chapterContent.id,
                    chapterContent: chapterContent.content,
                    version: chapterContent?.version,
                });
            } else {
                console.warn('未找到对应章节内容:', chapterId);
            }
        }, 2000),
        [editor, getCurrentChapterContent],
    );

    // 处理内容更新
    const handleDocumentChange = useCallback(
        (editor, transaction) => {
            console.log('文档内容发生变化');
            if (!transaction.docChanged) return;

            const content = editor.state.doc.textContent;
            debouncedUpdateWordCount(content);

            // 获取当前编辑位置最近的章节ID
            const { from } = editor.state.selection;
            const doc = editor.state.doc;
            const foundNodes = [];

            doc.nodesBetween(0, from, (node, pos) => {
                if (
                    node.type.name === 'heading' &&
                    (node.attrs.level === 2 || node.attrs.level === 3)
                ) {
                    foundNodes.push({ node, pos });
                }
            });

            // 从后往前找，获取最近的标题
            let currentChapterId = null;
            for (let i = foundNodes.length - 1; i >= 0; i--) {
                const { node } = foundNodes[i];
                if (node.attrs.id) {
                    currentChapterId = node.attrs.id;
                    console.log('找到当前章节ID:', currentChapterId);
                    break;
                }
            }

            // 更新当前编辑的章节ID
            if (currentChapterId) {
                editingChapterId.current = currentChapterId;
                console.log('准备调用自动保存，章节ID:', currentChapterId);
                debouncedAutoSave(currentChapterId);
            } else {
                console.warn('未找到可保存的章节ID');
            }
        },
        [debouncedUpdateWordCount, debouncedAutoSave],
    );

    // 使用useEffect监听编辑器变化
    useEffect(() => {
        if (editor) {
            editor.on('update', ({ editor, transaction }) => {
                handleDocumentChange(editor, transaction);
            });
        }
    }, [editor, handleDocumentChange]);

    // 创建编辑器滚动控制器
    const scroller = new EditorScroller(editor);

    // 编辑器内容状态
    const [content, setContent] = useState('');

    // 初始化编辑器内容
    useEffect(() => {
        if (editor && data) {
            const content = processContent(data);
            setContent(content);

            // 使用文本内容进行字数统计
            const wordCount = countWords(editor.state.doc.textContent);
            getWordCount(wordCount);
        }
    }, [editor, data]);

    // 内容变化时更新编辑器
    useEffect(() => {
        if (editor && content) {
            try {
                // 使用ProseMirror底层API处理内容
                editor.commands.setContent(content, {
                    preserveWhitespace: 'full', // 保留空白字符
                    parseOptions: {
                        preserveWhitespace: true, // 保留内容中的空白
                    },
                });

                // 延迟处理图片问题
                setTimeout(() => {
                    fixTrailingBreakIssue();
                }, 200);
            } catch (error) {
                console.error('设置编辑器内容时出错:', error);
            }

            // 使用文本内容进行字数统计
            const wordCount = countWords(editor.state.doc.textContent);
            getWordCount(wordCount);
        }
    }, [editor, content]);

    // 修复ProseMirror-trailingBreak问题
    const fixTrailingBreakIssue = () => {
        // 获取所有的trailingBreak元素
        const trailingBreaks = document.querySelectorAll('.ProseMirror-trailingBreak');
        console.log(`发现 ${trailingBreaks.length} 个 ProseMirror-trailingBreak 元素`);

        trailingBreaks.forEach((br) => {
            try {
                // 检查上一个元素是否为HTML注释
                let previousSibling = br.previousSibling;

                // 获取包含此br的段落元素
                const paragraphElement = br.closest('p');
                if (paragraphElement) {
                    // 遍历段落中的所有节点
                    const walker = document.createTreeWalker(
                        paragraphElement,
                        NodeFilter.SHOW_COMMENT | NodeFilter.SHOW_ELEMENT,
                        null,
                        false,
                    );

                    let node = walker.nextNode();
                    while (node) {
                        // 检查是否为注释节点
                        if (node.nodeType === Node.COMMENT_NODE) {
                            const comment = node.textContent || '';

                            // 检查注释中是否包含图片标签
                            if (comment.includes('<img')) {
                                console.log('在注释中找到图片标签:', comment);

                                // 解析图片标签
                                const tempDiv = document.createElement('div');
                                tempDiv.innerHTML = comment;
                                const imgElement = tempDiv.querySelector('img');

                                if (imgElement) {
                                    // 创建新的图片元素
                                    const newImg = document.createElement('img');

                                    // 复制所有属性
                                    Array.from(imgElement.attributes).forEach((attr) => {
                                        newImg.setAttribute(attr.name, attr.value);
                                    });

                                    // 设置特殊标记，防止被ProseMirror再次转换
                                    newImg.setAttribute('data-prosemirror-img-fixed', 'true');

                                    // 替换br标签
                                    if (br.parentNode) {
                                        br.parentNode.replaceChild(newImg, br);
                                        console.log('成功替换br为图片');

                                        // 通知编辑器内容已变化
                                        editor.commands.focus();
                                    }

                                    // 不再查找更多注释
                                    break;
                                }
                            }
                        }
                        node = walker.nextNode();
                    }
                }
            } catch (error) {
                console.error('修复trailingBreak时出错:', error);
            }
        });

        // 第二步：检查原始HTML中的图片
        if (typeof content === 'string' && content.includes('<img')) {
            console.log('在原始内容中检测到图片标签');

            // 解析所有图片标签
            const imgRegex = /<img[^>]+>/g;
            const matches = content.match(imgRegex) || [];

            if (matches.length > 0) {
                console.log(`找到 ${matches.length} 个图片标签`);

                // 检查编辑器中的图片数量
                const editorImages = document.querySelectorAll('.ProseMirror img');

                // 如果编辑器中的图片少于原始HTML中的图片
                if (editorImages.length < matches.length) {
                    console.log('编辑器中缺少图片，尝试恢复...');

                    // 解析原始HTML
                    const tempContainer = document.createElement('div');
                    tempContainer.innerHTML = content;

                    // 找到所有原始图片
                    const originalImages = tempContainer.querySelectorAll('img');

                    // 找到编辑器容器
                    const prosemirrorContainer = document.querySelector('.ProseMirror');
                    if (!prosemirrorContainer) return;

                    // 遍历正文段落，寻找可能错过的图片
                    const paragraphs = prosemirrorContainer.querySelectorAll('p');

                    paragraphs.forEach((p) => {
                        // 检查段落是否只包含br.ProseMirror-trailingBreak
                        if (
                            p.childNodes.length === 1 &&
                            p.firstChild.nodeName === 'BR' &&
                            p.firstChild.classList.contains('ProseMirror-trailingBreak')
                        ) {
                            // 尝试创建一个图片节点
                            // 从未渲染的图片中选择一个
                            for (let i = 0; i < originalImages.length; i++) {
                                const origImg = originalImages[i];
                                let alreadyRendered = false;

                                // 检查这个图片是否已经在编辑器中
                                for (let j = 0; j < editorImages.length; j++) {
                                    const editorImg = editorImages[j];
                                    if (
                                        editorImg.src === origImg.src ||
                                        editorImg.alt === origImg.alt ||
                                        editorImg.id === origImg.id
                                    ) {
                                        alreadyRendered = true;
                                        break;
                                    }
                                }

                                // 如果这个图片还没被渲染，则使用它
                                if (!alreadyRendered) {
                                    const newImg = document.createElement('img');

                                    // 复制所有属性
                                    Array.from(origImg.attributes).forEach((attr) => {
                                        newImg.setAttribute(attr.name, attr.value);
                                    });

                                    // 替换br标签
                                    p.replaceChild(newImg, p.firstChild);
                                    console.log(
                                        '成功恢复丢失的图片:',
                                        origImg.src || origImg.alt || origImg.id,
                                    );
                                    break;
                                }
                            }
                        }
                    });
                }
            }
        }
    };

    // 使编辑器中的标题不可编辑
    useEffect(() => {
        if (!editor) return;

        // 添加CSS样式使标题视觉上显示为不可编辑
        const style = document.createElement('style');
        style.innerHTML = `
                .ProseMirror h1, .ProseMirror h2, .ProseMirror h3, .ProseMirror h4, .ProseMirror h5, .ProseMirror h6 {
                    cursor: pointer; /* 改为指针，表示可点击 */
                    position: relative;
                }
                /* 暂时注释掉锁图标
                .ProseMirror h1::after, .ProseMirror h2::after, .ProseMirror h3::after,
                .ProseMirror h4::after, .ProseMirror h5::after, .ProseMirror h6::after {
                    content: '🔒';
                    font-size: 0.7em;
                    margin-left: 5px;
                    opacity: 0.5;
                    position: absolute;
                }
                */
            `;
        document.head.appendChild(style);

        // 移除之前的选择变化处理器，我们现在希望用户可以选择标题

        return () => {
            document.head.removeChild(style);
        };
    }, [editor]);

    // 检查工具选中状态
    const checkToolSelection = (toolId) => {
        const { from, to } = editor.state.selection;
        const hasSelection = from !== to;
        const node = hasSelection ? editor.state.doc.nodeAt(from) : null;

        // 文本工具检查
        const textTools = ['repetition', 'expand', 'shrink', 'rewrite'];
        if (textTools.includes(toolId)) {
            if (!hasSelection) {
                message.warning('请选择需要处理的文本');
                return false;
            }
            return true;
        }

        // 图片工具检查
        if (toolId === 'image') {
            if (!node || node.type.name !== 'image') {
                message.warning('请选择需要修改的图片');
                return false;
            }
            return true;
        }

        // 表格工具检查
        if (toolId === 'table') {
            if (node?.type.name === 'table') {
                return true;
            }

            const $pos = editor.state.doc.resolve(from);
            let depth = $pos.depth;
            let isInTable = false;

            while (depth > 0) {
                const node = $pos.node(depth);
                if (node.type.name === 'table') {
                    isInTable = true;
                    break;
                }
                depth--;
            }

            if (!isInTable) {
                message.warning('请选择需要修改的表格');
                return false;
            }
            return true;
        }

        return true;
    };

    // 检查选区是否包含标题
    const checkForHeadings = (from, to) => {
        let hasHeading = false;
        editor.state.doc.nodesBetween(from, to, (node) => {
            if (node.type.name === 'heading') {
                hasHeading = true;
                return false;
            }
        });
        return hasHeading;
    };

    // 获取选中的文本内容
    const getSelectedContent = (from, to) => {
        return editor.state.doc.textBetween(from, to, ' ');
    };

    // 获取选中的图片
    const getSelectedImage = () => {
        const { from } = editor.state.selection;
        const node = editor.state.doc.nodeAt(from);
        if (node?.type.name === 'image') {
            return {
                src: node.attrs.src,
                id: node.attrs.alt.split('-')[1],
            };
        }
        return null;
    };

    // 查找指定ID的图片位置
    const findImagePosition = (doc, id) => {
        let pos = -1;
        doc.descendants((node, position) => {
            if (node.type.name === 'image' && node.attrs?.alt?.split('-')[1] === id) {
                pos = position;
                return false;
            }
        });
        return pos;
    };

    // 暴露给父组件的方法
    useImperativeHandle(ref, () => ({
        setEditable(editable) {
            editor.setOptions({ editable });
        },
        scrollToHeadingText(text) {
            scroller.scrollToText(text);
        },
        scrollToId(id) {
            scroller.scrollToId(id);
        },
        // 获取编辑器当前内容
        getContent() {
            if (!editor) return null;

            const content = {
                abstract_zh: '',
                abstract_en: '',
                main_body: [],
            };

            // 遍历文档节点
            editor.state.doc.descendants((node, pos, parent) => {
                // 处理主体内容
                if (node.type.name === 'heading' && node.attrs.level === 2) {
                    // 处理摘要
                    if (node.textContent === '摘要') {
                        // 获取摘要内容
                        let currentPos = pos + node.nodeSize;
                        let currentNode = editor.state.doc.nodeAt(currentPos);
                        let abstractContent = '';
                        while (currentNode && currentNode.type.name === 'paragraph') {
                            // 获取原始文本内容，保留Markdown格式
                            const nodeText = currentNode.textContent;
                            abstractContent += (abstractContent ? '\n\n' : '') + nodeText;
                            currentPos += currentNode.nodeSize;
                            currentNode = editor.state.doc.nodeAt(currentPos);
                        }
                        content.abstract_zh = abstractContent;
                        return false;
                    }

                    // 处理英文摘要
                    if (node.textContent === 'Abstract') {
                        // 获取英文摘要内容
                        let currentPos = pos + node.nodeSize;
                        let currentNode = editor.state.doc.nodeAt(currentPos);
                        let abstractContent = '';
                        while (currentNode && currentNode.type.name === 'paragraph') {
                            // 获取原始文本内容，保留Markdown格式
                            const nodeText = currentNode.textContent;
                            abstractContent += (abstractContent ? '\n\n' : '') + nodeText;
                            currentPos += currentNode.nodeSize;
                            currentNode = editor.state.doc.nodeAt(currentPos);
                        }
                        content.abstract_en = abstractContent;
                        return false;
                    }

                    // 处理其他章节
                    const chapter = {
                        chapter: node.textContent,
                        id: node.attrs.id || '',
                        content: '',
                        sections: [],
                    };

                    // 获取章节内容直到下一个h2
                    let currentPos = pos + node.nodeSize;
                    let currentNode = editor.state.doc.nodeAt(currentPos);

                    while (
                        currentNode &&
                        !(currentNode.type.name === 'heading' && currentNode.attrs.level === 2)
                    ) {
                        if (currentNode.type.name === 'heading' && currentNode.attrs.level === 3) {
                            // 处理小节
                            const section = {
                                chapter: currentNode.textContent,
                                id: currentNode.attrs.id || '',
                                content: '',
                                subSections: [],
                            };
                            chapter.sections.push(section);
                        } else if (
                            currentNode.type.name === 'heading' &&
                            currentNode.attrs.level === 4
                        ) {
                            // 处理子小节
                            const lastSection = chapter.sections[chapter.sections.length - 1];
                            if (lastSection) {
                                lastSection.subSections.push({
                                    chapter: currentNode.textContent,
                                    id: currentNode.attrs.id || '',
                                    content: '',
                                });
                            }
                        } else if (currentNode.type.name === 'paragraph') {
                            // 处理文本内容
                            const text = currentNode.textContent;
                            if (chapter.sections.length === 0) {
                                chapter.content += `[AIML_TEXT]${text}[/AIML_TEXT]\n`;
                            } else {
                                const lastSection = chapter.sections[chapter.sections.length - 1];
                                if (lastSection.subSections.length === 0) {
                                    lastSection.content += `[AIML_TEXT]${text}[/AIML_TEXT]\n`;
                                } else {
                                    const lastSubSection =
                                        lastSection.subSections[lastSection.subSections.length - 1];
                                    lastSubSection.content += `[AIML_TEXT]${text}[/AIML_TEXT]\n`;
                                }
                            }
                        } else if (currentNode.type.name === 'table') {
                            // 处理表格
                            const tableId = currentNode.attrs.aimlTableId;

                            // 构建表格HTML，添加 data-table-id 属性
                            let tableHtml = `<table data-table-id="${tableId}">`;

                            // 遍历表格内容构建HTML
                            currentNode.content.forEach((row) => {
                                tableHtml += '<tr>';
                                row.content.forEach((cell) => {
                                    const tag = cell.type.name === 'tableHeader' ? 'th' : 'td';
                                    const colspan = cell.attrs.colspan
                                        ? ` colspan="${cell.attrs.colspan}"`
                                        : '';
                                    const rowspan = cell.attrs.rowspan
                                        ? ` rowspan="${cell.attrs.rowspan}"`
                                        : '';

                                    tableHtml += `<${tag}${colspan}${rowspan}>`;
                                    cell.content.forEach((paragraph) => {
                                        if (paragraph.content) {
                                            paragraph.content.forEach((text) => {
                                                tableHtml += text.text;
                                            });
                                        }
                                    });
                                    tableHtml += `</${tag}>`;
                                });
                                tableHtml += '</tr>';
                            });

                            tableHtml += '</table>';

                            // 使用 tableConverter 转换HTML为Markdown
                            const tableMarkdown = tableConverter.turndown(tableHtml);

                            // 构建最终的AIML_TABLE格式
                            const tableContent = `[AIML_TABLE id=${tableId}]${tableMarkdown}[/AIML_TABLE]`;

                            if (chapter.sections.length === 0) {
                                chapter.content += `${tableContent}\n`;
                            } else {
                                const lastSection = chapter.sections[chapter.sections.length - 1];
                                if (lastSection.subSections.length === 0) {
                                    lastSection.content += `${tableContent}\n`;
                                } else {
                                    const lastSubSection =
                                        lastSection.subSections[lastSection.subSections.length - 1];
                                    lastSubSection.content += `${tableContent}\n`;
                                }
                            }
                        } else if (currentNode.type.name === 'image') {
                            const imageId = currentNode.attrs.alt.split('-')[1];
                            const imageSrc = currentNode.attrs.src;
                            const imageContent = `[AIML_MERMAID id=${imageId}]${imageSrc}[/AIML_MERMAID]\n`;

                            if (chapter.sections.length === 0) {
                                chapter.content += imageContent;
                            } else {
                                const lastSection = chapter.sections[chapter.sections.length - 1];
                                if (lastSection.subSections.length === 0) {
                                    lastSection.content += imageContent;
                                } else {
                                    const lastSubSection =
                                        lastSection.subSections[lastSection.subSections.length - 1];
                                    lastSubSection.content += imageContent;
                                }
                            }
                        }

                        currentPos += currentNode.nodeSize;
                        currentNode = editor.state.doc.nodeAt(currentPos);
                    }

                    // 只有非摘要章节才添加到 main_body
                    if (node.textContent !== '摘要' && node.textContent !== 'Abstract') {
                        content.main_body.push(chapter);
                    }
                    return false;
                }

                return true;
            });

            if (content.abstract_zh) {
                content.abstract_zh = `${content.abstract_zh}`;
                // content.abstract_zh = `[AIML_TEXT]${content.abstract_zh}[/AIML_TEXT]`;
            }
            if (content.abstract_en) {
                // content.abstract_en = `[AIML_TEXT]${content.abstract_en}[/AIML_TEXT]`;
                content.abstract_en = `${content.abstract_en}`;
            }

            return content;
        },
        checkToolSelection: (toolId) => {
            return checkToolSelection(toolId);
        },
        handleButtonClick: (type) => {
            handleButtonClick(type);
        },
        getDocumentWordCount: () => {
            const content = editor.getHTML();
            return countWords(content);
        },
        getSelectedContent: () => {
            return getSelectedContent();
        },
        getSelectedImage: () => {
            return getSelectedImage();
        },
        // 更新编辑器文本内容的方法
        updateTextContent: (from, to, content) => {
            if (!editor) return false;

            const contentTrimmed = content.trim();
            if (!contentTrimmed) {
                console.error('Content is empty or only whitespace');
                return false;
            }

            // 计算内容长度差
            const oldContent = editor.state.doc.textBetween(from, to, '');
            const offset = contentTrimmed.length - oldContent.length;

            // 使用正确的编辑器命令
            editor.chain().focus().insertContentAt({ from, to }, contentTrimmed).run();

            // 计算新的光标位置
            const newPos = from + Math.min(offset, content.length);

            // 设置新的光标位置
            editor.chain().setTextSelection(newPos).run();
        },
        // 更新编辑器内指定id图片的src
        updateImageSrc: (oldId, newSrc) => {
            if (!oldId || !newSrc) {
                console.error('Invalid parameters for updateImageSrc');
                return false;
            }

            try {
                return editor
                    .chain()
                    .focus()
                    .command(({ tr }) => {
                        const pos = findImagePosition(tr.doc, oldId);
                        if (pos === -1) {
                            console.warn(`Image with id ${oldId} not found`);
                            return false;
                        }

                        if (!newSrc.match(/^(http|https|data:image)/)) {
                            console.error('Invalid image source URL');
                            return false;
                        }

                        tr.setNodeAttribute(pos, 'src', newSrc);

                        if (!tr.docChanged) {
                            console.warn('Transaction did not modify the document');
                            return false;
                        }

                        return true;
                    })
                    .run();
            } catch (error) {
                console.error('Error updating image source:', error);
                return false;
            }
        },
        updateTableHtml: (id, from, to, newContent) => {
            console.log('newContent', newContent);

            return editor.commands.command(({ tr }) => {
                const node = tr.doc.nodeAt(from);
                if (node?.type.name === 'table' && node.attrs.aimlTableId === id) {
                    if (
                        newContent.startsWith('[AIML_TABLE') &&
                        newContent.endsWith('[/AIML_TABLE]')
                    ) {
                        try {
                            const match = newContent.match(
                                /\[AIML_TABLE id=(.*?)\]([\s\S]*?)\[\/AIML_TABLE\]/s,
                            );
                            if (match) {
                                const [_, tableId, tableMarkdown] = match;

                                // 将Markdown转换为HTML
                                const tableConverter = new TurndownService({
                                    headingStyle: 'atx',
                                    codeBlockStyle: 'fenced',
                                });
                                tableConverter.use(gfm);

                                // 先将markdown转为HTML (我们已经有markdown，所以这一步其实是逆向操作)
                                // 使用showdown或类似库完成这一步
                                // 由于我们需要依赖编辑器已有的转换能力，这里简化处理
                                let htmlTable = '';
                                const rows = tableMarkdown.trim().split('\n');

                                if (rows.length >= 2) {
                                    htmlTable = '<table>';

                                    // 处理表头行
                                    const headerCells = rows[0]
                                        .split('|')
                                        .filter((cell) => cell.trim());
                                    if (headerCells.length > 0) {
                                        htmlTable += '<tr>';
                                        headerCells.forEach((cell) => {
                                            htmlTable += `<th>${cell.trim()}</th>`;
                                        });
                                        htmlTable += '</tr>';
                                    }

                                    // 跳过分隔行，处理数据行
                                    for (let i = 2; i < rows.length; i++) {
                                        const cells = rows[i]
                                            .split('|')
                                            .filter((cell) => cell.trim());
                                        if (cells.length > 0) {
                                            htmlTable += '<tr>';
                                            cells.forEach((cell) => {
                                                htmlTable += `<td>${cell.trim()}</td>`;
                                            });
                                            htmlTable += '</tr>';
                                        }
                                    }

                                    htmlTable += '</table>';

                                    // 删除旧表格
                                    tr.delete(from, to);

                                    // 使用编辑器的HTML解析器创建新表格
                                    const parser = editor.view.someProp('nodeViews');
                                    const schema = editor.schema;

                                    // 创建一个新的表格节点
                                    // 这需要根据实际的Tiptap表格结构来实现
                                    // 以下为简化示例，实际需要配合schema使用
                                    const tempDiv = document.createElement('div');
                                    tempDiv.innerHTML = htmlTable;
                                    const tableElement = tempDiv.querySelector('table');

                                    if (tableElement) {
                                        // 构建表格行和单元格
                                        const tableRows = [];

                                        for (let i = 0; i < tableElement.rows.length; i++) {
                                            const row = tableElement.rows[i];
                                            const rowCells = [];

                                            for (let j = 0; j < row.cells.length; j++) {
                                                const cell = row.cells[j];
                                                const isHeader =
                                                    cell.tagName.toLowerCase() === 'th';
                                                const cellType = isHeader
                                                    ? 'tableHeader'
                                                    : 'tableCell';

                                                // 创建单元格内容
                                                const content = schema.node(
                                                    'paragraph',
                                                    {},
                                                    schema.text(cell.textContent),
                                                );

                                                // 创建单元格
                                                const cellNode = schema.node(cellType, {}, [
                                                    content,
                                                ]);
                                                rowCells.push(cellNode);
                                            }

                                            // 创建行
                                            const rowNode = schema.node('tableRow', {}, rowCells);
                                            tableRows.push(rowNode);
                                        }

                                        // 创建表格
                                        const newTableNode = schema.node(
                                            'table',
                                            {
                                                aimlTableId: id,
                                            },
                                            tableRows,
                                        );

                                        // 插入新表格
                                        tr.insert(from, newTableNode);
                                        return true;
                                    }
                                }
                            }
                        } catch (error) {
                            console.error('Error updating table:', error);
                        }
                    }
                }
                return false;
            });
        },
    }));

    // 处理按钮点击
    const handleButtonClick = (type) => {
        const selection = editor.state.selection;
        let { from, to } = selection;

        // 标题重写用不需要检查标题选择
        if (type !== 'rewrite') {
            // 检查选中区域是否包含标题
            const hasHeading = checkForHeadings(from, to);
            if (hasHeading) {
                message.warning('不能选中标题内容！');
                return;
            }
        }

        // 检查工具类型的选区要求
        if (!checkToolSelection(type)) {
            return;
        }

        let chapterId = '';
        let id = '';
        let content;

        // 根据工具类型获取内容
        switch (type) {
            case 'image':
                content = getSelectedImage();
                id = content.id;

                const doc = editor.state.doc;
                const imagePos = editor.state.selection.from;
                const foundNodes = [];

                doc.nodesBetween(0, imagePos, (node, pos) => {
                    if (node.type.name === 'heading' || node.type.name === 'paragraph') {
                        foundNodes.push({ node, pos });
                    }
                });

                for (let i = foundNodes.length - 1; i >= 0; i--) {
                    const { node } = foundNodes[i];
                    if (node.attrs.id && !chapterId) {
                        chapterId = node.attrs.id;
                    }
                }
                break;
            case 'table':
                const tableParent = findParentNode((node) => node.type.name === 'table')(
                    editor.state.selection,
                );

                if (tableParent) {
                    // 获取表格的ID
                    const tableId = tableParent.node.attrs.aimlTableId;
                    id = tableId;

                    // 获取表格位置信息
                    const tablePos = tableParent.pos;
                    const tableSize = tableParent.node.nodeSize;
                    const tableFrom = tablePos;
                    const tableTo = tablePos + tableSize;

                    // 构建表格HTML
                    let tableHtml = '<table>';

                    // 遍历表格内容构建HTML
                    tableParent.node.content.forEach((row) => {
                        tableHtml += '<tr>';
                        row.content.forEach((cell) => {
                            const tag = cell.type.name === 'tableHeader' ? 'th' : 'td';
                            const colspan = cell.attrs.colspan
                                ? ` colspan="${cell.attrs.colspan}"`
                                : '';
                            const rowspan = cell.attrs.rowspan
                                ? ` rowspan="${cell.attrs.rowspan}"`
                                : '';

                            tableHtml += `<${tag}${colspan}${rowspan}>`;
                            cell.content.forEach((paragraph) => {
                                if (paragraph.content) {
                                    paragraph.content.forEach((text) => {
                                        tableHtml += text.text;
                                    });
                                }
                            });
                            tableHtml += `</${tag}>`;
                        });
                        tableHtml += '</tr>';
                    });

                    tableHtml += '</table>';

                    // 将HTML转换为Markdown
                    const tableMarkdown = tableConverter.turndown(tableHtml);

                    // 构建AIML_TABLE格式
                    content = `[AIML_TABLE id=${tableId}]${tableMarkdown}[/AIML_TABLE]`;

                    const doc = editor.state.doc;
                    const foundNodes = [];

                    doc.nodesBetween(0, tableParent.pos, (node, pos) => {
                        if (node.type.name === 'heading' || node.type.name === 'paragraph') {
                            foundNodes.push({ node, pos });
                        }
                    });

                    for (let i = foundNodes.length - 1; i >= 0; i--) {
                        const { node } = foundNodes[i];
                        if (node.attrs.id && !chapterId) {
                            chapterId = node.attrs.id;
                        }
                    }

                    // 保存表格位置信息
                    from = tableFrom;
                    to = tableTo;
                }
                break;
            default:
                if (from !== to) {
                    const doc = editor.state.doc;
                    const foundNodes = [];

                    // 从选中位置向上遍历文档，找到最近的标题
                    doc.nodesBetween(0, from, (node, pos) => {
                        if (
                            (node.type.name === 'heading' &&
                                (node.attrs.level === 2 || node.attrs.level === 3)) ||
                            node.attrs.level === 4
                        ) {
                            foundNodes.push({ node, pos });
                        }
                    });

                    // 从后往前找，这样可以找到最近的标题
                    for (let i = foundNodes.length - 1; i >= 0; i--) {
                        const { node } = foundNodes[i];
                        if (node.attrs.id) {
                            chapterId = node.attrs.id;
                            break;
                        }
                    }
                }
                content = getSelectedContent(from, to);
        }

        console.log('chapterId--->', chapterId);

        if (content) {
            onOpenModal({
                type,
                content,
                from,
                to,
                chapterId,
                id,
            });
        }
    };

    // 重写章节
    const rewriteChapter = () => {
        const { from } = editor.state.selection;
        const $from = editor.state.doc.resolve(from);

        // 查找当前标题节点
        let headingDepth = -1;
        let headingPos = -1;
        let headingNode = null;

        // 查找当前标题
        for (let d = $from.depth; d >= 0; d--) {
            const node = $from.node(d);
            if (
                node.type.name === 'heading' &&
                (node.attrs.level === 3 || node.attrs.level === 4)
            ) {
                headingDepth = d;
                headingNode = node;
                headingPos = $from.before(d);
                break;
            }
        }

        if (!headingNode || headingPos === -1) {
            return;
        }

        const doc = editor.state.doc;
        const headingEnd = headingPos + headingNode.nodeSize;

        // 收集章节内容
        let contents = [];
        let contentEnd = null;
        let originalContent = '';

        // 从当前位置开始向后遍历
        let pos = headingEnd;
        while (pos < doc.content.size) {
            const $pos = doc.resolve(pos);
            const node = doc.nodeAt(pos);

            if (!node) break;

            // 如果遇到了同级或更高级的标题，说明到了章节边界
            if (node.type.name === 'heading') {
                if (
                    (headingNode.attrs.level === 3 && node.attrs.level <= 3) ||
                    (headingNode.attrs.level === 4 && node.attrs.level <= 4)
                ) {
                    contentEnd = pos;
                    break;
                }
            }

            // 只收集顶层段落
            if ($pos.depth === 0 && node.type.name === 'paragraph') {
                const paragraphContent = node.textContent.trim();
                if (paragraphContent) {
                    contents.push({
                        type: node.type.name,
                        from: pos,
                        to: pos + node.nodeSize,
                        content: paragraphContent,
                    });
                    // 收集原始内容
                    originalContent += paragraphContent + '\n';
                }
            }

            pos += node.nodeSize;
        }

        if (contents.length === 0) {
            return;
        }

        // 如果没有找到下一个标题，使用最后一个段落的结束位置
        const finalContentEnd = contentEnd || contents[contents.length - 1].to;

        // 调用模态框
        if (headingNode.attrs.id && headingEnd < finalContentEnd) {
            console.log('rewrite', {
                type: 'rewrite',
                content: contents,
                from: headingEnd,
                to: finalContentEnd,
                chapterId: headingNode.attrs.id,
                originalContent: originalContent.trim(), // 添加原始内容
            });

            onOpenModal({
                type: 'rewrite',
                content: contents,
                from: headingEnd,
                to: finalContentEnd,
                chapterId: headingNode.attrs.id,
                originalContent: originalContent.trim(), // 添加原始内容
            });
        }
    };

    // 添加图片渲染调试钩子
    useEffect(() => {
        if (!editor) return;

        // 清理函数
        return () => {
            // 清理所有 blob URL
            if (window.mermaidBlobUrls) {
                console.log(`清理 ${Object.keys(window.mermaidBlobUrls).length} 个 blob URL`);
                Object.values(window.mermaidBlobUrls).forEach((url) => {
                    try {
                        URL.revokeObjectURL(url);
                    } catch (error) {
                        console.error('清理 blob URL 时出错:', error);
                    }
                });
                window.mermaidBlobUrls = {};
            }
        };
    }, [editor]);

    if (!editor) {
        return null;
    }

    return (
        <div className="academic-editor">
            {editor && (
                <BubbleMenu
                    className="bubble-menu"
                    tippyOptions={{
                        duration: 100,
                        hideOnClick: true,
                        trigger: 'manual',
                    }}
                    editor={editor}
                    shouldShow={shouldShowBubbleMenu}
                >
                    {/* 修图按钮 */}
                    {shouldShowButtons.image(editor) && (
                        <button className="menu-button" onClick={() => handleButtonClick('image')}>
                            修图
                        </button>
                    )}

                    {/* 修表按钮 */}
                    {shouldShowButtons.table(editor) && (
                        <button className="menu-button" onClick={() => handleButtonClick('table')}>
                            修表
                        </button>
                    )}

                    {/* 重写本章按钮 */}
                    {shouldShowButtons.rewrite(editor) && (
                        <button className="menu-button" onClick={rewriteChapter}>
                            重写本章
                        </button>
                    )}

                    {/* 文本工具按钮组 */}
                    {shouldShowButtons.textTools(
                        editor,
                        editor.state.selection.from,
                        editor.state.selection.to,
                    ) && (
                        <>
                            <button
                                className="menu-button"
                                onMouseDown={() => handleButtonClick('repetition')}
                            >
                                降重
                            </button>
                            <button
                                className="menu-button"
                                onClick={() => handleButtonClick('expand')}
                            >
                                扩写
                            </button>
                            <button
                                className="menu-button"
                                onClick={() => handleButtonClick('shrink')}
                            >
                                缩写
                            </button>
                        </>
                    )}
                </BubbleMenu>
            )}
            <EditorContent
                editor={editor}
                onKeyDown={(e) => {
                    // 检查当前是否在标题内
                    if (editor && editor.isActive('heading')) {
                        // 允许选择操作键 (方向键、Home、End、Page Up/Down)
                        const allowedKeys = [
                            'ArrowUp',
                            'ArrowDown',
                            'ArrowLeft',
                            'ArrowRight',
                            'Home',
                            'End',
                            'PageUp',
                            'PageDown',
                        ];

                        // 允许组合键 (Ctrl+A, Shift+方向键等)
                        if (
                            ((e.ctrlKey || e.metaKey) && e.key === 'a') ||
                            (e.shiftKey && allowedKeys.includes(e.key)) ||
                            allowedKeys.includes(e.key)
                        ) {
                            return true; // 允许这些键操作
                        }

                        // 阻止可能修改内容的键
                        e.preventDefault();
                        return false;
                    }

                    // 添加保护逻辑：阻止删除标题
                    if ((e.key === 'Backspace' || e.key === 'Delete') && editor) {
                        const { selection } = editor.state;
                        const { from, to, empty } = selection;

                        // 如果是选区删除，检查选区是否包含标题
                        if (!empty) {
                            let hasHeading = false;
                            editor.state.doc.nodesBetween(from, to, (node) => {
                                if (node.type.name === 'heading') {
                                    hasHeading = true;
                                    return false;
                                }
                            });

                            if (hasHeading) {
                                e.preventDefault();
                                return false;
                            }
                        } else {
                            // 单个光标位置删除检查
                            const doc = editor.state.doc;

                            // Backspace检查
                            if (e.key === 'Backspace' && from > 0) {
                                // 检查是否会删除到标题节点
                                const prevNode = doc.resolve(Math.max(0, from - 1));
                                for (let i = prevNode.depth; i > 0; i--) {
                                    if (prevNode.node(i).type.name === 'heading') {
                                        e.preventDefault();
                                        return false;
                                    }
                                }
                            }

                            // Delete检查
                            if (e.key === 'Delete' && to < doc.content.size) {
                                // 检查是否会删除到标题节点
                                const nextNode = doc.resolve(Math.min(doc.content.size, to + 1));
                                for (let i = nextNode.depth; i > 0; i--) {
                                    if (nextNode.node(i).type.name === 'heading') {
                                        e.preventDefault();
                                        return false;
                                    }
                                }
                            }
                        }
                    }
                }}
            />
        </div>
    );
});

export default memo(AcademicEditor);
